<script lang="ts">
    import { type Selected } from 'bits-ui';
    import { useLocaleContext, usePropertiesContext } from '$lib/context';
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { createKey, createVariantMap, getUniqueOptions } from '$lib/util/productUtil';
    import { t } from 'svelte-i18n';

    const properties = usePropertiesContext();
    const lang = useLocaleContext();

    let classes = '';
    export { classes as class };

    export let variants: ApiSchemas['Product'][];
    export let productVariant: ApiSchemas['Product'];

    $: variantMap = createVariantMap(variants);

    let selectedSize: Selected<{ id: string; productNumber: string }>;
    $: selectedSize = getSelectedSize(productVariant);

    function getSelectedSize(product: ApiSchemas['Product']) {
        const sizeOption: ApiSchemas['Product']['options'] = product.options.find(
            (option: ApiSchemas['Product']['options']) => option.groupId === properties.size.id
        );

        return {
            label: sizeOption.name ?? sizeOption.translated.name,
            value: {
                id: sizeOption.id,
                productNumber: product.productNumber
            }
        };
    }

    let selectedYear: Selected<{ id: string; productNumber: string }>;
    $: selectedYear = getSelectedYear(productVariant);

    function getSelectedYear(product: ApiSchemas['Product']) {
        const yearOption: ApiSchemas['Product']['options'] = product.options.find(
            (option: ApiSchemas['Product']['options']) => option.groupId === properties.year.id
        );

        return {
            label: yearOption.name ?? yearOption.translated.name,
            value: {
                id: yearOption.id,
                productNumber: product.productNumber
            }
        };
    }

    let availableSizes: Selected<{ id: string; productNumber: string }>[];
    $: if (variants && selectedYear?.value?.id) {
        availableSizes = getAvailableSizes(variants, selectedYear.value.id);
    }

    function getAvailableSizes(variants: ApiSchemas['Product'][], yearId: string) {
        return getUniqueOptions(
            variants.filter(
                (variant) =>
                    yearId ===
                    variant.options.find(
                        (option: ApiSchemas['Product']['options']) => option.groupId === properties.year.id
                    ).id
            ),
            properties.size.id
        ).map((option) => ({
            label: option.name ?? option.translated.name,
            value: variantMap[createKey([option.id, yearId])]
        }));
    }

    let availableYears: Selected<{ id: string; productNumber: string }>[];
    $: if (variants && selectedSize?.value?.id) {
        availableYears = getAvailableYears(variants, selectedSize.value.id);
    }

    function getAvailableYears(variants: ApiSchemas['Product'][], sizeId: string) {
        return getUniqueOptions(
            variants.filter(
                (variant) =>
                    sizeId ===
                    variant.options.find(
                        (option: ApiSchemas['Product']['options']) => option.groupId === properties.size.id
                    ).id
            ),
            properties.year.id
        ).map((option) => ({
            label: option.name ?? option.translated.name,
            value: variantMap[createKey([sizeId, option.id])]
        }));
    }
</script>

<div class={classes}>
    <div class="flex flex-wrap gap-x-8 gap-y-5 text-neutral-500">
        {#if availableYears && !availableYears.find((year) => (year.label ?? 'unknown').length > 4)}
            <div class="flex-col flex gap-1">
                {$t('shop.product.year')}
                <div class="flex w-full flex-wrap text-primary gap-3">
                    {#each availableYears as year}
                        <a
                            data-sveltekit-replacestate
                            data-sveltekit-noscroll
                            class="px-5 py-1.5 border-primary border {year.value.id === productVariant.id &&
                                'bg-primary text-neutral-50'}"
                            href="/{lang}/product/{year.value.productNumber}">{year.label}</a
                        >
                    {/each}
                </div>
            </div>
        {/if}
        {#if availableSizes}
            <div class="flex flex-col gap-1">
                {$t('shop.product.size')}
                <div class="flex text-primary gap-3">
                    {#each availableSizes as size}
                        <a
                            data-sveltekit-replacestate
                            data-sveltekit-noscroll
                            class="px-5 py-1.5 border-primary border {size.value.id === productVariant.id &&
                                'bg-primary text-neutral-50'}"
                            href="/{lang}/product/{size.value.productNumber}">{size.label}</a
                        >
                    {/each}
                </div>
            </div>
        {/if}
    </div>
</div>
